import { TextField, Autocomplete, Box } from "@mui/material";
import { Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useShoppingCart } from "../context/ShoppingCartContext";
import {
  getSyncOrderCheckoutContext,
  useCheckoutConfig,
} from "./checkoutConfig";
import CheckoutLegalConsentLinks from "./CheckoutLegalConsentLinks";

import usePageLanguage from "../../_default/hooks/usePageLanguage";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useEffect, useState } from "react";
import { ComponentType } from "react";
import React from "react";
import { countries } from "../utilities/countries";

interface Props {
  show: boolean;
  setShow: (val: boolean) => void;
  onCancel?: () => void;
}

const languageTranslations = {
  et: {
    buyer: "OSTJA",
    first_name: "Eesnimi",
    last_name: "Perenimi",
    phone: "Mobiil",
    email: "E-post",
    agree_terms: "NÕUSTUN TINGIMUSTEGA JA SALVESTAN",
    organization: "Äriklient",
    client: "Eraklient",
    legal_name: "Juriidiline nimi",
    reg_nr: "Registrikood",
    KMKR_nr: "KMKR nr",
    address: "Aadress",
    no_results: "Tulemusi ei leitud",
    country: "Riik",
    email_required: "E-posti väli on kohustuslik",
    email_invalid: "E-posti aadress on vale",
    phone_required: "Mobiili väli on kohustuslik",
    phone_invalid: "Mobiili number on puudu",
    loading: "LAADIMINE...",
    representative_first_name: "Esindaja eesnimi",
    representative_last_name: "Esindaja perenimi",
    representative_first_name_required: "Esindaja eesnimi on kohustuslik",
    representative_last_name_required: "Esindaja perenimi on kohustuslik",
    representative_pair_need_firstname: "Täida ka esindaja eesnimi",
    representative_pair_need_surname: "Täida ka esindaja perenimi",
  },
  en: {
    buyer: "BUYER",
    first_name: "First name",
    last_name: "Last name",
    phone: "Phone",
    email: "Email",
    agree_terms: "AGREE WITH TERMS AND SAVE",
    organization: "Organization",
    client: "Client",
    legal_name: "Legal name",
    reg_nr: "Registration number",
    KMKR_nr: "KMKR number",
    address: "Address",
    no_results: "No results found",
    country: "Country",
    email_required: "E-mail is required",
    email_invalid: "E-mail is invalid",
    phone_required: "Phone is required",
    phone_invalid: "Phone number is incomplete",
    loading: "LOADING...",
    representative_first_name: "Representative first name",
    representative_last_name: "Representative last name",
    representative_first_name_required: "Representative first name is required",
    representative_last_name_required: "Representative last name is required",
    representative_pair_need_firstname: "Also enter representative first name",
    representative_pair_need_surname: "Also enter representative last name",
  },
};
// Define the CountryType interface
interface CountryType {
  code: string;
  label: string;
  phone: string;
}

const BuyerModal: React.FC<{
  show: boolean;
  setShow:
    | React.Dispatch<React.SetStateAction<boolean>>
    | ((val: boolean) => void);
  onCancel?: () => void;
}> = ({ show, setShow, onCancel }) => {
  let lang = usePageLanguage();

  const {
    buyer,
    setBuyer,
    syncOrder,
    setShowCheckoutErrorModal,
  } = useShoppingCart();
  const checkoutConfig = useCheckoutConfig();
  const checkoutContext = getSyncOrderCheckoutContext(checkoutConfig.delivery);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [type, setType] = useState(buyer.type || "buyer");
  const [orgHistoricalNames, setOrgHistoricalNames] = useState(
    buyer.organization_historical_names || ""
  );
  const [buyerSelectedFlag, setBuyerSelectedFlag] = useState(
    `${"https://flagcdn.com/w20/ee.png"}`
  );
  const [organizationSelectedFlag, setOrganizationSelectedFlag] = useState(
    `${"https://flagcdn.com/w20/ee.png"}`
  );

  useEffect(() => {
    const initialBuyerCountry = countries.find(
      (c) => c.code === buyer.country_code
    );
    const initialOrganizationCountry = countries.find(
      (c) => c.code === buyer.organization_country_code
    );
    if (initialBuyerCountry) {
      setBuyerSelectedFlag(
        `https://flagcdn.com/w20/${initialBuyerCountry.code.toLowerCase()}.png`
      );
    }
    if (initialOrganizationCountry) {
      setOrganizationSelectedFlag(
        `https://flagcdn.com/w20/${initialOrganizationCountry.code.toLowerCase()}.png`
      );
    }
  }, [buyer.country_code, buyer.organization_country_code]);

  const handleBuyerCountryChange = (
    event: any,
    newValue: CountryType | null
  ) => {
    setBuyerSelectedFlag(
      newValue
        ? `https://flagcdn.com/w20/${newValue.code.toLowerCase()}.png`
        : ""
    );
  };

  const [organizationOptions, setOrganizationOptions] = useState<any>([]);

  const validatePhoneNumber = (value: string) =>
    !!value && matchIsValidTel(value);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "all",
    shouldUnregister: true,
    defaultValues: {
      type: type,
      firstname: buyer.firstname ?? "",
      surname: buyer.surname ?? "",
      phone: buyer.phone ?? "+372",
      email: buyer.email ?? "",
      language: lang ?? "et",
      organization_language: lang ?? "et",
      country_code: buyer.country_code ?? "EE",
      organization_country_code: buyer.organization_country_code ?? "EE",
      organization_name: buyer.organization_name ?? "",
      organization_reg_nr: buyer.organization_reg_nr ?? "",
      organization_KMKR_number: buyer.organization_KMKR_number ?? "",
      organization_phone: buyer.organization_phone ?? "+372",
      organization_email: buyer.organization_email ?? "",
      organization_address: buyer.organization_address ?? "",
      organization_historical_names: buyer.organization_historical_names ?? "",
      organization_ariregister_status:
        buyer.organization_ariregister_status ?? "",
      organization_ariregister_url: buyer.organization_ariregister_url ?? "",
      representativeFirstname: buyer.representativeFirstname ?? "",
      representativeSurname: buyer.representativeSurname ?? "",
    },
  });

  const [orgStatus, setOrgStatus] = useState(
    buyer.organization_ariregister_status || ""
  );
  const [orgUrl, setOrgUrl] = useState(
    buyer.organization_ariregister_url || ""
  );

  const handleOrganizationCountryChange = (
    newValue: CountryType | null,
    organizationCountryCode: string
  ) => {
    if (organizationCountryCode !== "EE") {
      setOrganizationOptions([]);
    }

    const orgPhone = getValues("organization_phone");
    if (orgPhone && newValue && !matchIsValidTel(orgPhone)) {
      setValue("organization_phone", `+${newValue.phone}`);
    }

    setOrganizationSelectedFlag(
      newValue
        ? `https://flagcdn.com/w20/${newValue.code.toLowerCase()}.png`
        : ""
    );
  };

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    const buyerData = {
      ...buyer,
      ...data,
      type: type,
      organization_historical_names: orgHistoricalNames
        ? orgHistoricalNames
        : "",
      organization_ariregister_status: orgStatus ? orgStatus : "",
      organization_ariregister_url: orgUrl ? orgUrl : "",
      ...(checkoutConfig.b2bContactPerson.visible
        ? {}
        : {
            representativeFirstname: "",
            representativeSurname: "",
          }),
    };
    setBuyer(buyerData);

    const orderResult = await syncOrder({
      buyerData,
      checkoutContext,
      includeB2bContactPerson: checkoutConfig.b2bContactPerson.visible,
    });
    setIsSubmitting(false);
    if (!orderResult.ok) {
      setShowCheckoutErrorModal(true);
      return;
    }
    setShow(false);
  };

  const handleOrganizationQuery = (query: string) => {
    const url = `https://ariregister.rik.ee/est/api/autocomplete?q=${query}&results=10`;

    fetch(url)
      .then((res) => res.json())
      .then((res) => {
        if (!res.data) {
          return;
        }
        const data = [...res.data].map((item: any) => {
          return {
            label: item.name,
            organization_name: item.name,
            organization_reg_nr: item.reg_code,
            organization_KMKR_number: "",
            organization_phone: "+372",
            organization_email: "",
            organization_address: `${item.legal_address}, ${item.zip_code}`,
            organization_historical_names:
              item.historical_names.length > 0
                ? `${item.historical_names
                    .map((historicalName: any) => historicalName.name)
                    .join(", ")}`
                : "",
            organization_ariregister_status: item.status ? item.status : "",
            organization_ariregister_url: item.url ? item.url : "",
          };
        });

        setOrganizationOptions(data);
      });
  };

  // Define the type for your form's field names
  type FieldName =
    | "type"
    | "firstname"
    | "surname"
    | "phone"
    | "email"
    | "organization_name"
    | "organization_reg_nr"
    | "organization_KMKR_number"
    | "organization_phone"
    | "organization_email"
    | "organization_address"
    | "representativeFirstname"
    | "representativeSurname";

  const setFieldValue = (fieldName: FieldName, value: any) => {
    setValue(fieldName, value);
  };

  const validateRepresentativeFirstname = (
    value: string,
    formValues: Record<string, unknown>
  ) => {
    const first = (value ?? "").trim();
    const last = String(formValues.representativeSurname ?? "").trim();
    if (!first && !last) return true;
    if (first && last) return true;
    if (!first && last) {
      return languageTranslations[lang]?.representative_pair_need_firstname;
    }
    return true;
  };

  const validateRepresentativeSurname = (
    value: string,
    formValues: Record<string, unknown>
  ) => {
    const first = String(formValues.representativeFirstname ?? "").trim();
    const last = (value ?? "").trim();
    if (!first && !last) return true;
    if (first && last) return true;
    if (first && !last) {
      return languageTranslations[lang]?.representative_pair_need_surname;
    }
    return true;
  };

  const switchBuyerType = (nextType: "buyer" | "organization") => {
    if (!checkoutConfig.b2bContactPerson.visible) {
      setType(nextType);
      return;
    }

    const firstname = getValues("firstname") ?? "";
    const surname = getValues("surname") ?? "";
    const representativeFirstname = getValues("representativeFirstname") ?? "";
    const representativeSurname = getValues("representativeSurname") ?? "";

    setType(nextType);

    if (nextType === "organization") {
      setValue(
        "representativeFirstname",
        firstname || representativeFirstname,
        { shouldValidate: false }
      );
      setValue("representativeSurname", surname || representativeSurname, {
        shouldValidate: false,
      });
    } else {
      setValue("firstname", representativeFirstname || firstname, {
        shouldValidate: false,
      });
      setValue("surname", representativeSurname || surname, {
        shouldValidate: false,
      });
    }
  };

  // Handle modal close via X button or backdrop click
  const handleModalClose = () => {
    if (onCancel) {
      onCancel();
    } else {
      setShow(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        contentClassName="modal-600 buyerModal shop-controlled-border-radius"
      >
        <Modal.Header
          closeButton
          style={{
            borderTopLeftRadius: "var(--shop-border-radius)",
            borderTopRightRadius: "var(--shop-border-radius)",
          }}
        >
          <Modal.Title>
            <span className="badge badge-square badge-primary me-3">2</span>
            <span className="fw-bold">{languageTranslations[lang]?.buyer}</span>
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                marginTop: "0px",
                marginBottom: "20px",
              }}
            >
              <div
                onClick={() => {
                  switchBuyerType("buyer");
                }}
                className={`
            buyerModal__btn ${
              type == "buyer" ? "buyerModal__btn--active" : ""
            }`}
              >
                {languageTranslations[lang]?.client}
              </div>

              <div
                onClick={() => {
                  switchBuyerType("organization");
                }}
                className={`buyerModal__btn ${
                  type == "organization" ? "buyerModal__btn--active" : ""
                }`}
              >
                {languageTranslations[lang]?.organization}
              </div>
            </div>

            {type === "organization" && (
              <div className="row">
                <div className="col-md-4">
                  <Controller
                    name="organization_country_code"
                    rules={{ required: true }}
                    control={control}
                    defaultValue={buyer.organization_country_code || "EE"} // Set default value here
                    render={({ field }) => (
                      <Autocomplete
                        options={countries}
                        className="text-field-color"
                        value={
                          countries.find((c) => c.code === field.value) || null
                        }
                        onChange={(event, newValue: any) => {
                          const countryCode = newValue ? newValue.code : "";
                          field.onChange(countryCode);
                          handleOrganizationCountryChange(newValue, countryCode);
                        }}
                        freeSolo
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                          >
                            <img
                              loading="lazy"
                              width="20"
                              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                              alt=""
                            />
                            {option.label}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={languageTranslations[lang]?.country}
                            variant="standard"
                            error={
                              errors.organization_country_code?.type ===
                              "required"
                            }
                            InputProps={{
                              ...params.InputProps,
                              autoComplete: "new-password",
                              startAdornment: organizationSelectedFlag && (
                                <img
                                  loading="lazy"
                                  width="26"
                                  src={organizationSelectedFlag}
                                  srcSet={`${organizationSelectedFlag.replace(
                                    "w20",
                                    "w40"
                                  )} 2x`}
                                  alt=""
                                  style={{ marginRight: 8 }}
                                />
                              ),
                              // Hide the country name in the input field
                              readOnly: true,
                              value: "",
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </div>

                <div className="col-md-8">
                  <Controller
                    name="organization_name"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      // <TextField
                      //   className="text-field-color"
                      //   InputLabelProps={{
                      //     style: { color: "#333", fontSize: "14px" },
                      //   }}
                      //   fullWidth
                      //   // Make tab automatically active
                      //   autoFocus={true}
                      //   label={languageTranslations[lang]?.legal_name}
                      //   variant="standard"
                      //   value={field.value}
                      //   onChange={field.onChange}
                      //   inputRef={field.ref}
                      //   error={errors.organization_name?.type == "required"}
                      // />

                      <Autocomplete
                        className="text-field-color"
                        options={organizationOptions}
                        value={field.value}
                        freeSolo
                        // noOptionsText={languageTranslations[lang]?.no_results}
                        // run if selected
                        onInputChange={(event, newInputValue) => {
                          //field.onChange(newInputValue);

                          // Get the selected value from the options

                          // setFieldValue("organization_name", newInputValue);
                          const selectedValue = organizationOptions.find(
                            (option) =>
                              option.organization_name == newInputValue
                          );

                          if (!selectedValue) {
                            setFieldValue("organization_name", newInputValue);
                          }

                          // Update organization_name, organization_reg_nr, organization_KMKR_number, organization_phone, organization_email, organization_address

                          if (selectedValue) {
                            setFieldValue("organization_name", newInputValue);
                            setFieldValue(
                              "organization_reg_nr",
                              selectedValue.organization_reg_nr
                            );
                            setFieldValue(
                              "organization_KMKR_number",
                              selectedValue.organization_KMKR_number
                            );
                            setFieldValue(
                              "organization_email",
                              selectedValue.organization_email
                            );
                            setFieldValue(
                              "organization_address",
                              selectedValue.organization_address
                            );

                            setOrgHistoricalNames(
                              selectedValue.organization_historical_names
                            );

                            setOrgStatus(
                              selectedValue.organization_ariregister_status
                            );

                            setOrgUrl(
                              selectedValue.organization_ariregister_url
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            autoFocus={true}
                            InputLabelProps={{
                              style: { color: "#333", fontSize: "14px" },
                            }}
                            onChange={(e) => {
                              const countryCodeField = getValues(
                                "organization_country_code"
                              );

                              if (countryCodeField === "EE") {
                                handleOrganizationQuery(e.target.value);
                              } else {
                                setOrganizationOptions([]);
                              }
                            }}
                            label={languageTranslations[lang]?.legal_name}
                            error={errors.organization_name?.type == "required"}
                          />
                        )}
                      />
                    )}
                  />
                </div>

                <div className="col-md-12">
                  <Controller
                    name="organization_address"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.address}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={errors.organization_address?.type == "required"}
                      />
                    )}
                  />
                </div>

                <div className="col-md-3">
                  <Controller
                    name="organization_reg_nr"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.reg_nr}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={errors.organization_reg_nr?.type == "required"}
                      />
                    )}
                  />
                </div>

                <div className="col-md-4">
                  <Controller
                    name="organization_KMKR_number"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <TextField
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.KMKR_nr}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={
                          errors.organization_KMKR_number?.type == "required"
                        }
                      />
                    )}
                  />
                </div>

                <div className="col-md-5">
                  <Controller
                    name="organization_phone"
                    rules={{
                      required: languageTranslations[lang]?.phone_required,
                      validate: {
                        phoneValidation: (value) =>
                          validatePhoneNumber(value) ||
                          languageTranslations[lang]?.phone_invalid,
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <MuiTelInput
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.phone}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={!!errors.organization_phone}
                        helperText={
                          errors.organization_phone
                            ? errors.organization_phone.message
                            : ""
                        }
                        FormHelperTextProps={{
                          sx: {
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                </div>

                <div className="col-md-12">
                  <Controller
                    name="organization_email"
                    rules={{
                      required: languageTranslations[lang]?.email_required,
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: languageTranslations[lang]?.email_invalid,
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        FormHelperTextProps={{
                          sx: {
                            fontSize: "14px",
                          },
                        }}
                        label={languageTranslations[lang]?.email}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={!!errors.organization_email}
                        helperText={
                          errors.organization_email
                            ? errors.organization_email.message
                            : ""
                        }
                      />
                    )}
                  />
                </div>


                {checkoutConfig.b2bContactPerson.visible && (
                  <>
                    <div className="col-md-6">
                      <Controller
                        name="representativeFirstname"
                        rules={{
                          required: checkoutConfig.b2bContactPerson.required
                            ? languageTranslations[lang]
                                ?.representative_first_name_required
                            : false,
                          validate: {
                            representativePair: validateRepresentativeFirstname,
                          },
                        }}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            className="text-field-color"
                            InputLabelProps={{
                              style: { color: "#333", fontSize: "14px" },
                            }}
                            fullWidth
                            FormHelperTextProps={{
                              sx: {
                                fontSize: "14px",
                              },
                            }}
                            label={
                              languageTranslations[lang]?.representative_first_name
                            }
                            variant="standard"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event);
                              void trigger("representativeSurname");
                            }}
                            inputRef={field.ref}
                            error={!!errors.representativeFirstname}
                            helperText={
                              errors.representativeFirstname
                                ? errors.representativeFirstname.message
                                : ""
                            }
                          />
                        )}
                      />
                    </div>

                    <div className="col-md-6">
                      <Controller
                        name="representativeSurname"
                        rules={{
                          required: checkoutConfig.b2bContactPerson.required
                            ? languageTranslations[lang]
                                ?.representative_last_name_required
                            : false,
                          validate: {
                            representativePair: validateRepresentativeSurname,
                          },
                        }}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            className="text-field-color"
                            InputLabelProps={{
                              style: { color: "#333", fontSize: "14px" },
                            }}
                            fullWidth
                            FormHelperTextProps={{
                              sx: {
                                fontSize: "14px",
                              },
                            }}
                            label={
                              languageTranslations[lang]?.representative_last_name
                            }
                            variant="standard"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event);
                              void trigger("representativeFirstname");
                            }}
                            inputRef={field.ref}
                            error={!!errors.representativeSurname}
                            helperText={
                              errors.representativeSurname
                                ? errors.representativeSurname.message
                                : ""
                            }
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {type === "buyer" && (
              <div className="row">
                <div className="col-md-6">
                  <Controller
                    name="firstname"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        className="text-field-color"
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        // Make tab automatically active
                        autoFocus={true}
                        label={languageTranslations[lang]?.first_name}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={errors.firstname?.type == "required"}
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="surname"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.last_name}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={errors.surname?.type == "required"}
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="email"
                    rules={{
                      required: languageTranslations[lang]?.email_required,
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: languageTranslations[lang]?.email_invalid,
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        FormHelperTextProps={{
                          sx: {
                            fontSize: "14px",
                          },
                        }}
                        label={languageTranslations[lang]?.email}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        error={!!errors.email}
                        helperText={errors.email ? errors.email.message : ""}
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="phone"
                    rules={{
                      required: languageTranslations[lang]?.phone_required,
                      validate: {
                        phoneValidation: (value) =>
                          validatePhoneNumber(value) ||
                          languageTranslations[lang]?.phone_invalid,
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <MuiTelInput
                        className=""
                        InputLabelProps={{
                          style: { color: "#333", fontSize: "14px" },
                        }}
                        fullWidth
                        label={languageTranslations[lang]?.phone}
                        variant="standard"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={!!errors.phone}
                        helperText={errors.phone ? errors.phone.message : ""}
                        FormHelperTextProps={{
                          sx: {
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="country_code"
                    rules={{ required: true }}
                    control={control}
                    defaultValue={buyer.country_code || "EE"}
                    render={({ field }) => (
                      <Autocomplete
                        options={countries}
                        value={
                          countries.find((c) => c.code === field.value) || null
                        }
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue.code : ""); // Handle onChange to update field value
                          handleBuyerCountryChange(event, newValue); // Handle country change
                        }}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                          >
                            <img
                              loading="lazy"
                              width="20"
                              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                              alt=""
                            />
                            {option.label}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <>
                            <TextField
                              {...params}
                              InputLabelProps={{
                                style: { color: "#333", fontSize: "14px" },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                autoComplete: "new-password",
                                startAdornment: buyerSelectedFlag && (
                                  <img
                                    loading="lazy"
                                    width="26"
                                    src={buyerSelectedFlag}
                                    srcSet={`${buyerSelectedFlag.replace(
                                      "w20",
                                      "w40"
                                    )} 2x`}
                                    alt=""
                                    style={{ marginRight: 8 }}
                                  />
                                ),
                              }}
                              sx={{
                                "& .MuiInputBase-root": {
                                  marginBottom: 0,
                                },
                              }}
                              FormHelperTextProps={{
                                sx: {
                                  fontSize: "14px",
                                },
                              }}
                              fullWidth
                              label={languageTranslations[lang]?.country}
                              variant="standard"
                              error={errors.country_code?.type == "required"}
                            />
                          </>
                        )}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            <CheckoutLegalConsentLinks
              legalLinks={checkoutConfig.legalLinks}
              locale={lang}
            />
          </Modal.Body>
          <Modal.Footer className="">
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                color: "#fff",
                fontWeight: "600",
              }}
              className="btn w-100 shop-controlled-border-radius shop-controlled-color"
            >
              {isSubmitting ? (
                <div className="d-flex align-items-center justify-content-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">
                      {languageTranslations[lang]?.loading}
                    </span>
                  </div>
                  {languageTranslations[lang]?.loading}
                </div>
              ) : (
                languageTranslations[lang]?.agree_terms
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default BuyerModal;
